import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { moderationService, PendingModerationPost, ModeratePostRequest } from '../../services/moderationService';
import { PostModerationStatus } from '../../services/postService';
import { Modal } from '../../components/ui/Modal';
import { Avatar } from '../../components/Avatar';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';

export const AdminModerationPage: React.FC = () => {
  const [posts, setPosts] = useState<PendingModerationPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState<PendingModerationPost | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    loadPendingPosts();
  }, [currentPage]);

  const loadPendingPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await moderationService.getPendingPosts(currentPage, pageSize);
      setPosts(response.posts || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Không thể tải danh sách bài viết chờ duyệt');
      console.error('Failed to load pending posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (post: PendingModerationPost) => {
    try {
      setError(null);
      const request: ModeratePostRequest = {
        status: PostModerationStatus.APPROVED
      };
      await moderationService.moderatePost(post.id, request);
      await loadPendingPosts();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Không thể duyệt bài viết');
      console.error('Failed to approve post:', err);
    }
  };

  const handleReject = async () => {
    if (!selectedPost) return;
    try {
      setError(null);
      const request: ModeratePostRequest = {
        status: PostModerationStatus.REJECTED,
        reason: rejectReason.trim() || undefined
      };
      await moderationService.moderatePost(selectedPost.id, request);
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedPost(null);
      await loadPendingPosts();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Không thể từ chối bài viết');
      console.error('Failed to reject post:', err);
    }
  };

  const openRejectModal = (post: PendingModerationPost) => {
    setSelectedPost(post);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const getModerationBadge = (post: PendingModerationPost) => {
    if (post.isSensitive && post.isViolent) {
      return (
        <div className="flex gap-2">
          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
            Nhạy cảm
          </span>
          <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full">
            Bạo lực
          </span>
        </div>
      );
    }
    if (post.isSensitive) {
      return (
        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
          Nhạy cảm
        </span>
      );
    }
    if (post.isViolent) {
      return (
        <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full">
          Bạo lực
        </span>
      );
    }
    return null;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Kiểm duyệt bài viết</h1>
            <p className="text-gray-600 mt-1">
              Tổng cộng: {totalElements} bài viết chờ duyệt
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <svg
              className="w-16 h-16 mx-auto text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Không có bài viết chờ duyệt
            </h3>
            <p className="text-gray-500">Tất cả bài viết đã được xử lý</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start space-x-4">
                    <Avatar
                      user={{
                        userId: post.authorId,
                        displayName: post.authorDisplayName,
                        avatarUrl: post.authorAvatarUrl || undefined
                      }}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-gray-900">
                            {post.authorDisplayName}
                          </h3>
                          <span className="text-sm text-gray-500">
                            @{post.authorUsername}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatDistanceToNow(new Date(post.createdAt), {
                              addSuffix: true,
                              locale: vi
                            })}
                          </span>
                        </div>
                        {getModerationBadge(post)}
                      </div>

                      <p className="text-gray-700 mb-4 whitespace-pre-wrap break-words">
                        {post.content}
                      </p>

                      {post.media && post.media.length > 0 && (
                        <div className={`grid gap-3 mb-4 ${
                          post.media.length === 1 ? 'grid-cols-1' : 
                          post.media.length === 2 ? 'grid-cols-2' : 
                          'grid-cols-2 md:grid-cols-3'
                        }`}>
                          {post.media.slice(0, 6).map((media, idx) => (
                            <div key={idx} className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                              {media.mediaType === 'video' ? (
                                <video
                                  src={media.mediaUrl}
                                  className="w-full h-auto max-h-96 object-contain"
                                  controls
                                />
                              ) : (
                                <img
                                  src={media.mediaUrl}
                                  alt={`Media ${idx + 1}`}
                                  className="w-full h-auto max-h-96 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => window.open(media.mediaUrl, '_blank')}
                                />
                              )}
                              {media.classificationLabel && (
                                <div className="absolute top-2 left-2 bg-gradient-to-r from-red-600 to-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                                  {media.classificationLabel}
                                  {media.classificationConfidence && (
                                    <span className="ml-1">
                                      ({(media.classificationConfidence * 100).toFixed(0)}%)
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                          {post.media.length > 6 && (
                            <div className="flex items-center justify-center bg-gray-100 rounded-lg border border-gray-300 p-4">
                              <span className="text-gray-600 font-semibold">
                                +{post.media.length - 6} ảnh khác
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {post.mood && (
                        <div className="text-sm text-gray-500 mb-4">
                          <span className="font-semibold">Tâm trạng:</span> {post.mood}
                          {post.moodScore && (
                            <span className="ml-2">
                              ({(post.moodScore * 100).toFixed(0)}%)
                            </span>
                          )}
                        </div>
                      )}

                      <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => handleApprove(post)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                          Duyệt
                        </button>
                        <button
                          onClick={() => openRejectModal(post)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                        >
                          Từ chối
                        </button>
                        <div className="flex-1"></div>
                        <div className="text-xs text-gray-500">
                          ID: {post.id}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Trước
                </button>
                <span className="text-sm text-gray-600">
                  Trang {currentPage + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Sau
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectReason('');
          setSelectedPost(null);
        }}
        title="Từ chối bài viết"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lý do từ chối (tùy chọn)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500 focus:border-transparent min-h-[100px] resize-none"
              placeholder="Nhập lý do từ chối bài viết này..."
            />
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowRejectModal(false);
                setRejectReason('');
                setSelectedPost(null);
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={handleReject}
              className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
            >
              Từ chối
            </button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};


